import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaperPlaneTilt, EnvelopeSimple, CaretDown, FilePdf, FileXls } from "@phosphor-icons/react";
// v1.29.29 — WhatsApp option icon switched to lucide MessageCircle (green)
// per the Party Ledger top-bar redesign.
import { MessageCircle, Send } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

const fmtInr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n || 0);

/**
 * v1.24.3 — One-tap Share Ledger button on the Party Ledger page.
 * Options:
 *   • Email PDF to party
 *   • Email XLSX to party
 *   • WhatsApp party (free wa.me link with a pre-typed summary)
 * All routes fall back gracefully if the party's contact data is missing.
 */
export default function ShareLedgerButton({ party, closingBalance }) {
    const [busy, setBusy] = useState(false);
    const [emailDlg, setEmailDlg] = useState(null); // { format, to }

    if (!party?.id) return null;

    const partyEmail = party.email || "";
    const partyPhone = (party.phone || "").replace(/\D/g, "");

    const sendEmail = async (format, overrideTo = null) => {
        const to = overrideTo || partyEmail;
        if (!to) { setEmailDlg({ format, to: "" }); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/parties/${party.id}/email-ledger`, { to, format });
            toast.success(`Ledger ${format.toUpperCase()} sent to ${data.sent_to}`);
            setEmailDlg(null);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const openWhatsapp = () => {
        if (!partyPhone) {
            toast.error("This party doesn't have a phone number saved. Add one in Parties → Edit.");
            return;
        }
        const till = new Date(Date.now() - 864e5).toLocaleDateString("en-GB");
        // v1.27.93 — Label uses standard Receivable / Payable / Settled.
        const direction = closingBalance > 0 ? "Receivable" : closingBalance < 0 ? "Payable" : "Settled";
        const bal = fmtInr(Math.abs(closingBalance || 0));
        const msg =
            `Hello ${party.name || ""},\n\n` +
            `Your account statement with MD Impex India till dated ${till}:\n` +
            `• Closing balance: ${bal} · ${direction}\n\n` +
            `We have also emailed the full PDF/Excel to ${partyEmail || "your registered email"}. ` +
            `Please review and let us know for any discrepancy.\n\nRegards,\nMICM · MD Impex India`;
        // Indian phone numbers need country code prefix for wa.me
        const number = partyPhone.length === 10 ? `91${partyPhone}` : partyPhone;
        const url = `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <>
            <DropdownMenu>
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <DropdownMenuTrigger asChild>
                                <Button data-testid="share-ledger-btn" variant="outline" disabled={busy} aria-label="Share Ledger" className="border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100">
                                    <Send size={15} className="text-emerald-700" />
                                    <CaretDown size={11} className="ml-1" />
                                </Button>
                            </DropdownMenuTrigger>
                        </TooltipTrigger>
                        <TooltipContent>Share Ledger</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
                <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        Send to <span className="font-mono text-slate-700">{party.name}</span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="share-email-pdf" onClick={() => sendEmail("pdf")} className="cursor-pointer">
                        <EnvelopeSimple size={14} className="mr-2 text-blue-600" />
                        <div className="flex-1">
                            <div className="text-sm">Email <b>PDF</b></div>
                            <div className="text-[10px] text-slate-500 truncate">
                                {partyEmail ? `to ${partyEmail}` : "no email — will ask"}
                            </div>
                        </div>
                        <FilePdf size={14} className="text-rose-600" />
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid="share-email-xlsx" onClick={() => sendEmail("xlsx")} className="cursor-pointer">
                        <EnvelopeSimple size={14} className="mr-2 text-blue-600" />
                        <div className="flex-1">
                            <div className="text-sm">Email <b>Excel</b></div>
                            <div className="text-[10px] text-slate-500 truncate">
                                {partyEmail ? `to ${partyEmail}` : "no email — will ask"}
                            </div>
                        </div>
                        <FileXls size={14} className="text-emerald-600" />
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="share-whatsapp" onClick={openWhatsapp} className="cursor-pointer">
                        <MessageCircle size={15} className="mr-2 text-[#25D366]" />
                        <div className="flex-1">
                            <div className="text-sm">WhatsApp summary</div>
                            <div className="text-[10px] text-slate-500 truncate">
                                {partyPhone ? `+${partyPhone.length === 10 ? "91 " : ""}${partyPhone}` : "no phone saved"}
                            </div>
                        </div>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={!!emailDlg} onOpenChange={(v) => !v && setEmailDlg(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Send Ledger {emailDlg?.format?.toUpperCase()}</DialogTitle>
                    </DialogHeader>
                    <div>
                        <Label className="text-xs">Recipient email</Label>
                        <Input
                            type="email"
                            data-testid="share-email-input"
                            value={emailDlg?.to || ""}
                            onChange={(e) => setEmailDlg((s) => ({ ...s, to: e.target.value }))}
                            placeholder="party@example.com"
                            className="mt-1"
                            autoFocus
                        />
                        <div className="mt-2 text-[11px] text-slate-500">
                            The party has no email saved. Type one to send it once (or add it permanently in Parties → Edit).
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEmailDlg(null)}>Cancel</Button>
                        <Button
                            data-testid="share-email-confirm"
                            onClick={() => sendEmail(emailDlg.format, emailDlg.to)}
                            disabled={busy || !emailDlg?.to}
                            className="bg-blue-600 text-white hover:bg-blue-700"
                        >
                            {busy ? "Sending…" : "Send"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
